/**
 * Permissions API service for Whakahaere Safety Management System
 * Uses /permissions/ endpoint (ADR-048 tiered permission model).
 *
 * Typed `@tiaki/http-client` surface per ADR 113 §Decision.
 */

import { client } from '../lib/http-client';
import { CapabilityMeta, Permission, PermissionCategoryMeta } from '../types/role';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

/**
 * `GET /api/v2/permissions/` response: the paginated envelope plus the ADR-048
 * amendment (D7) top-level `categories` and `capabilities` blocks and a
 * `category` + `level` on each `results[]` entry.
 */
export interface PermissionsListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: Permission[];
  categories: PermissionCategoryMeta[];
  capabilities: CapabilityMeta[];
}

/**
 * Fetch the full permission catalog (tiered categories, capabilities, and every
 * permission with its category/level). Requested with a high limit so the
 * `results[]` set is complete — the picker needs every id ⇄ codename pair to
 * project a role's flat permission set onto the tiered model.
 */
export async function getPermissions(): Promise<PermissionsListResponse> {
  const result = await client.GET('/api/v2/permissions/', {
    params: { query: { limit: 1000 } as never },
  });
  return unwrap(result) as unknown as PermissionsListResponse;
}
